import { pipe, Identity, Option, ReadonlyArray } from "fp";
import React from "react";

import { Branch } from "~/Branch";
import { Reference } from "~/Data";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import * as Table from "~/Table";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import * as Source from "./Source";
import moment from "moment";

export * as Response from "./Response";
export * as Source from "./Source";

export type Event = Identity & {
  readonly sources: Source.Sources.Sources;
  readonly distance?: number;
  readonly location?: string;
  readonly notes?: string;
};

/** An `Event` is uniquely identified by its `status` and `interval` */
export type Identity = Time.Interval.Stopped & {
  readonly status: Driver.Status.Status;
};

export type UniqueEvent = Log.Event.Event & {
  readonly id: string;
};

export const id = ({ status, interval: { start, stop } }: Identity): string =>
  `${status}${start.dateTime}${stop.dateTime}`;

export const canEditHOS = ({
  sources,
  status,
}: Pick<Event, "sources" | "status">): boolean =>
  pipe(
    sources.original?.type ?? sources.current.type,
    (sourceType) => sourceType === Source.Type.tracker(),
    (isTrackerLog) => isTrackerLog && status === Driver.Status.drive(),
    (isTrackedDriveTime) => !isTrackedDriveTime
  );

export const href = () => "duty-status" as const;

export type EventProps = Data.Cache.Get.Get<Event, Event | undefined> & {
  readonly event?: Reference.Reference;
};

export const useFromActiveLog = function (
  route: Log.Route,
  { missing, found, event }: EventProps
): Event | undefined {
  return Log.Data.useActiveLogWhen(route, {
    unavailable: () => missing(),
    available: ({ events }) =>
      pipe(
        events,
        ReadonlyArray.findFirst(
          (e) => event !== undefined && id(e) === event.id
        ),
        Option.getOrElse(() => missing()),
        (event) => (event ? found(event) : missing())
      ),
  });
};

export const useAllFromActiveLog = (
  route: Log.Route
): readonly UniqueEvent[] => {
  return Log.Data.useActiveLogWhen<readonly UniqueEvent[]>(route, {
    unavailable: () => [],
    available: ({ events }) =>
      pipe(
        events,
        ReadonlyArray.map<Event, UniqueEvent>((event) => ({
          id: Log.Event.id(event),
          ...event,
        })),
        ReadonlyArray.sort<UniqueEvent>(
          Order.fromCompare(Time.Interval.compare)
        )
      ),
  });
};

export const Component = (route: Log.Route) => {
  return (
    <Layout.Section.Component>
      <Layout.Block.Component all>
        <Typography.H2.Component id={href()}>
          Duty Status
        </Typography.H2.Component>
      </Layout.Block.Component>
      <Log.Table.Component>
        {pipe(
          Log.Data.useFromRoute(route),
          Remote.when({
            success: () => <Events.Component {...route} />,
            loading: () => (
              <Table.Wide.Loading.Component colSpan={Log.Table.colSpan()} />
            ),
            error: (error) => (
              <Table.Wide.ErrorMessage.Component colSpan={Log.Table.colSpan()}>
                {error.message}
              </Table.Wide.ErrorMessage.Component>
            ),
          })
        )}
      </Log.Table.Component>
    </Layout.Section.Component>
  );
};

export namespace State {
  export type State = Data.Store.Store<Event>;
  export const initial = (): State => Data.Store.initial();
}

namespace Events {
  export const Component = (route: Log.Route) => {
    const revisionDrawerControl = Log.Revision.Form.Drawer.use(route);

    const onRevise = (event: Event, driverZone?: Time.Zone) => {
      // Adjust revision for timezone
      const revision: Event = {
        ...event,
        interval: {
          start: Time.DateTime.fromNumber(
            Time.DateTime.toMoment(event.interval.start)
              .tz(driverZone?.zone ?? Time.Zone.local().zone, false)
              .tz(moment.tz.guess(), true)
              .valueOf()
          ),
          stop: Time.DateTime.fromNumber(
            Time.DateTime.toMoment(event.interval.stop)
              .tz(driverZone?.zone ?? Time.Zone.local().zone, false)
              .tz(moment.tz.guess(), true)
              .valueOf()
          ),
        },
      };
      revisionDrawerControl.show(revision);
    };

    const [homeOffice, zone] = Driver.useFromReference({
      ...route.driver,
      found: (driver) => [driver.homeOffice, driver.time],
      missing: () => [undefined, undefined],
    });

    const branch = Branch.FromLocation.useOrUndefined(homeOffice?.location);
    const allowRevisions = Time.DateTime.isBeforeCurrentDay(route, zone);
    return pipe(
      useAllFromActiveLog(route),
      ReadonlyArray.map((event) => {
        return (
          <Row.Component
            route={route}
            key={event.id}
            event={event}
            onRevise={onRevise}
            disabled={!allowRevisions}
            tooltip={
              allowRevisions ? undefined : "Same day revisions are not allowed."
            }
          />
        );
      }),
      (events) => (
        <>
          <Log.Revision.Form.Drawer {...branch} {...revisionDrawerControl} />
          <Body.Component events={events} />
        </>
      )
    );
  };

  namespace Body {
    export const Component: React.Fn<{
      readonly events: readonly React.ReactNode[];
    }> = ({ events: [first, ...rest] }) =>
      first ? (
        <Table.Body.Component>{[first, ...rest]}</Table.Body.Component>
      ) : (
        <Table.Wide.Empty.Component colSpan={Log.Table.colSpan()} />
      );
  }
}

namespace Row {
  export type Props = {
    readonly route: Log.Route;
    readonly event: Data.Reference.Reference & Log.Event.Event;
    readonly onRevise: (
      revision: Log.Event.Event | Log.Suggestion.Suggestion,
      timezone?: Time.Zone
    ) => void;
    readonly disabled?: boolean;
    readonly tooltip?: string;
  };
  export const Component: React.Fn<Props> = ({
    route,
    event,
    onRevise,
    disabled,
    tooltip,
  }) => (
    <Table.Row.Component>
      <Log.Table.Row.Component {...(event as Log.Table.Row)} />
      <Log.Table.Revise.Component
        route={route}
        event={event}
        onRevise={onRevise}
        disabled={disabled}
        tooltip={tooltip}
      />
    </Table.Row.Component>
  );
}
